/**
 * Thesis Changelog Component
 *
 * Displays a collapsible changelog of thesis version history.
 * Shows changes between consecutive versions in a concise format.
 *
 * Features:
 * - Collapsed by default, tap to expand
 * - Shows only versions with actual changes
 * - Change summary format: "Conviction: 5 → 3 stars, Core Holding → Short-Term"
 * - Thesis text preview (2 lines with "Show more" / "Show less")
 * - Theme-aware styling (Bauhaus and Kinetic)
 * - Full i18n support
 */

import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Platform, LayoutAnimation } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { Card } from '../ui/Card';
import type { PositionThesis } from '../../services/api/positionThesisApi';
import { parseThesisText } from '../../types/thesis';
import {
  detectThesisChanges,
  formatVersionDate,
  truncateThesisText,
} from '../../utils/thesisChangeDetection';

export interface ThesisChangelogProps {
  versions: PositionThesis[];
  currency?: string;
}

interface ChangeEntry {
  versionId: number | string; // Can be number for historical versions or string for "current" entry
  updatedAt: string;
  changeSummaries: string[];
  thesisText: string;
  hasThesisText: boolean;
  isTruncated: boolean;
  fullText: string;
}

export function ThesisChangelog({ versions, currency = '' }: ThesisChangelogProps) {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedTextIds, setExpandedTextIds] = useState<Set<number | string>>(new Set());

  // Process versions into change entries
  const changeEntries = useMemo(() => {
    const entries: ChangeEntry[] = [];

    // First, add the current (latest) version with changes from previous version
    if (versions.length > 0) {
      const currentVersion = versions[0];
      const parsed = parseThesisText(currentVersion.thesis_text);
      const statementText = parsed.statement || '';
      const { truncated, isTruncated } = truncateThesisText(statementText);

      // Detect changes from previous version to current (what changed to get here)
      let changeSummaries: string[] = [];
      if (versions.length > 1) {
        const previousVersion = versions[1]; // The version before current
        // previousVersion = what changed FROM, currentVersion = what changed TO
        const changes = detectThesisChanges(previousVersion, currentVersion, t);
        if (changes) {
          changeSummaries = changes.map((c) => c.text);
        }
      }

      entries.push({
        versionId: `current-${currentVersion.id}`, // Unique key for current entry
        updatedAt: `${t('portfolio.current', { defaultValue: 'Current' })} • ${formatVersionDate(currentVersion.updated_at)}`,
        changeSummaries,
        thesisText: truncated,
        hasThesisText: statementText.length > 0,
        isTruncated,
        fullText: statementText,
      });
    }

    // Then, add change entries for historical versions (skip index 0 since we already handled it)
    for (let i = 1; i < versions.length; i++) {
      const olderVersion = versions[i];
      // Compare with next version (which is chronologically newer)
      const newerVersion = i + 1 < versions.length ? versions[i + 1] : null;

      if (!newerVersion) continue;

      // olderVersion = what changed FROM, newerVersion = what changed TO
      const changes = detectThesisChanges(olderVersion, newerVersion, t);
      if (!changes) continue;

      const parsed = parseThesisText(olderVersion.thesis_text);
      const statementText = parsed.statement || '';
      const { truncated, isTruncated } = truncateThesisText(statementText);

      entries.push({
        versionId: olderVersion.id,
        updatedAt: formatVersionDate(olderVersion.updated_at),
        changeSummaries: changes.map((c) => c.text),
        thesisText: truncated,
        hasThesisText: statementText.length > 0,
        isTruncated,
        fullText: statementText,
      });
    }

    return entries;
  }, [versions, t]);

  const toggleExpanded = () => {
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
    setIsExpanded(!isExpanded);
  };

  const toggleTextExpansion = (versionId: number) => {
    const newExpanded = new Set(expandedTextIds);
    if (newExpanded.has(versionId)) {
      newExpanded.delete(versionId);
    } else {
      newExpanded.add(versionId);
    }
    setExpandedTextIds(newExpanded);
  };

  if (changeEntries.length === 0) {
    return null;
  }

  return (
    <Card style={styles.container}>
      {/* Header - Always Visible */}
      <TouchableOpacity
        onPress={toggleExpanded}
        activeOpacity={0.7}
        style={[
          styles.header,
          {
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.backgroundElevated,
          },
        ]}
      >
        <View style={styles.headerContent}>
          <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
            {t('portfolio.changelog', { defaultValue: 'Changelog' })}
          </Text>
          <Text style={[styles.headerCount, { color: theme.colors.textSecondary }]}>
            {changeEntries.length}{' '}
            {t(changeEntries.length === 1 ? 'portfolio.entry' : 'portfolio.entries', {
              defaultValue: changeEntries.length === 1 ? 'entry' : 'entries',
            })}
          </Text>
        </View>
        <Text style={[styles.expandIcon, { color: theme.colors.textSecondary }]}>
          {isExpanded ? '▼' : '▶'}
        </Text>
      </TouchableOpacity>

      {/* Change Entries - Visible when expanded */}
      {isExpanded && (
        <View style={styles.entriesContainer}>
          {changeEntries.map((entry, index) => {
            const isTextExpanded = expandedTextIds.has(entry.versionId);
            const displayText = isTextExpanded ? entry.fullText : entry.thesisText;

            return (
              <View
                key={entry.versionId}
                style={[
                  styles.entry,
                  index < changeEntries.length - 1 && {
                    borderBottomColor: theme.colors.border,
                    borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
                  },
                ]}
              >
                {/* Date and change summary */}
                <View style={styles.entryHeader}>
                  <Text style={[styles.entryDate, { color: theme.colors.textSecondary }]}>
                    {entry.updatedAt}
                  </Text>
                </View>

                {/* Change summaries */}
                <View style={styles.changesContainer}>
                  {entry.changeSummaries.map((change, idx) => (
                    <Text key={idx} style={[styles.changeText, { color: theme.colors.text }]}>
                      {idx > 0 && ', '}
                      {change}
                    </Text>
                  ))}
                </View>

                {/* Thesis text preview */}
                {entry.hasThesisText && (
                  <View style={styles.thesisPreviewContainer}>
                    <Text
                      style={[styles.thesisPreview, { color: theme.colors.textSecondary }]}
                      numberOfLines={isTextExpanded ? undefined : 2}
                    >
                      "{displayText}"
                    </Text>
                    {entry.isTruncated && (
                      <TouchableOpacity
                        onPress={() => toggleTextExpansion(entry.versionId)}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                      >
                        <Text style={[styles.showMoreLink, { color: theme.colors.primary }]}>
                          {isTextExpanded
                            ? t('portfolio.showLess', { defaultValue: 'Show less' })
                            : t('portfolio.showMore', { defaultValue: 'Show more' })}
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            );
          })}
        </View>
      )}
    </Card>
  );
}

/**
 * Create themed styles dynamically
 */
function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      marginTop: theme.spacing.lg,
      marginBottom: theme.spacing.lg,
    },
    header: {
      flexDirection: 'row' as 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      paddingHorizontal: theme.spacing.lg,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.id === 'bauhaus' ? 0 : theme.radii.md,
    },
    headerContent: {
      flexDirection: 'row' as 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
    },
    headerTitle: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    headerCount: {
      fontSize: theme.typography.fontSizeSm,
      opacity: 0.7,
    },
    expandIcon: {
      fontSize: theme.typography.fontSizeSm,
    },
    entriesContainer: {
      paddingTop: theme.spacing.md,
    },
    entry: {
      marginBottom: theme.spacing.md,
      paddingBottom: theme.spacing.md,
    },
    entryHeader: {
      marginBottom: theme.spacing.xs,
    },
    entryDate: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    changesContainer: {
      marginBottom: theme.spacing.xs,
    },
    changeText: {
      fontSize: theme.typography.fontSizeSm,
      lineHeight: 20,
    },
    thesisPreviewContainer: {
      marginTop: theme.spacing.xs,
    },
    thesisPreview: {
      fontSize: theme.typography.fontSizeXs,
      fontStyle: 'italic',
      lineHeight: 18,
    },
    showMoreLink: {
      fontSize: theme.typography.fontSizeXs,
      fontWeight: theme.typography.fontWeightSemibold,
      marginTop: 2,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
